import DelayedSkeleton from "@src/lib/ui/DelayedSkeleton";
import Link from "next/link";
import useOnWindowResize from "@src/lib/hooks/useOnWindowResize";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import React, {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from "react";
import { Route } from "nextjs-routes";
import useHover from "@src/lib/hooks/useHover";
import { cn } from "@src/lib/utils/cn";
import { cva } from "class-variance-authority";

export type TextType =
  | "title"
  | "H1"
  | "H2"
  | "H3"
  | "H4"
  | "P1"
  | "P2"
  | "P3"
  | "P4"
  | "training-content";
export type TextFontWeight = "Regular" | "Medium" | "SemiBold" | "Bold";

export const textVariants = cva("transition-all duration-150 ease-bezier", {
  variants: {
    variant: {
      title: "leading-[42px] text-[42px]",
      H1: "leading-[34px] text-[24px] tracking-[-0.019em]",
      H2: "leading-[28px] text-[20px] tracking-[-0.017em]",
      H3: "leading-[25px] text-[18px] tracking-[-0.014em]",
      H4: "leading-[22px] text-[18px] tracking-[-0.014em]",
      P1: "leading-[22px] text-[16px] tracking-[-0.01em]",
      P2: "leading-[20px] text-[14px] tracking-[-0.006em]",
      P3: "leading-[17px] text-[12px] tracking-0",
      P4: "leading-[10px] text-[10px] tracking-0",
      ["training-content"]: "leading-[125%] text-[20px] tracking-[-.035em]",
    },
    fontWeight: {
      Regular: "font-normal",
      Medium: "font-medium",
      SemiBold: "font-semibold",
      Bold: "font-bold",
    },
    inline: {
      true: "inline-block",
      false: "",
    },
    clickable: {
      true: "hover:cursor-pointer",
      false: "",
    },
  },
  defaultVariants: {
    variant: "P1",
    fontWeight: "Regular",
    inline: false,
    clickable: false,
  },
});

export type TextV4Variant =
  | "page-title"
  | "display"
  | "display-bold"
  | "body-title"
  | "body-description"
  | "compact-title"
  | "compact-description"
  | "helper-title"
  | "helper-description"
  | "fine-print-title"
  | "fine-print-description"
  | "micro-title";

export const textV4Variants = cva("transition-all duration-150 ease-bezier", {
  variants: {
    variant: {
      "page-title":
        "font-sans-v4 text-2xl-v4 leading-s-v4 tracking-m-v4 font-semibold-v4",
      display:
        "font-sans-v4 text-xl-v4 leading-m-v4 tracking-m-v4 font-medium-v4",
      "display-bold":
        "font-sans-v4 text-xl-v4 leading-m-v4 tracking-m-v4 font-bold-v4",
      "body-title":
        "font-sans-v4 text-m-v4 leading-2xl-v4 tracking-m-v4 font-medium-v4",
      "body-description":
        "font-sans-v4 text-m-v4 leading-2xl-v4 tracking-m-v4 font-normal-v4",
      "compact-title":
        "font-sans-v4 text-s-v4 leading-m-v4 tracking-m-v4 font-medium-v4",
      "compact-description":
        "font-sans-v4 text-s-v4 leading-m-v4 tracking-m-v4 font-normal-v4",
      "helper-title":
        "font-sans-v4 text-xs-v4 leading-m-v4 tracking-m-v4 font-medium-v4",
      "helper-description":
        "font-sans-v4 text-xs-v4 leading-l-v4 tracking-m-v4 font-normal-v4",
      "fine-print-title":
        "font-sans-v4 text-xs-v4 leading-m-v4 tracking-m-v4 font-medium-v4",
      "fine-print-description":
        "font-sans-v4 text-2xs-v4 leading-l-v4 tracking-m-v4 font-normal-v4",
      "micro-title":
        "font-sans-v4 text-3xs-v4 leading-l-v4 tracking-s-v4 font-medium-v4",
    },
    inline: {
      true: "inline-block",
      false: "",
    },
    clickable: {
      true: "hover:cursor-pointer",
      false: "",
    },
  },
  defaultVariants: {
    inline: false,
    clickable: false,
  },
});

export const textV4VariantMetrics: Record<
  TextV4Variant,
  { fontSize: number; lineHeight: number }
> = {
  "page-title": { fontSize: 24, lineHeight: 30 },
  display: { fontSize: 20, lineHeight: 26 },
  "display-bold": { fontSize: 20, lineHeight: 26 },
  "body-title": { fontSize: 16, lineHeight: 23.2 },
  "body-description": { fontSize: 16, lineHeight: 23.2 },
  "compact-title": { fontSize: 14, lineHeight: 18.2 },
  "compact-description": { fontSize: 14, lineHeight: 18.2 },
  "helper-title": { fontSize: 13, lineHeight: 16.9 },
  "helper-description": { fontSize: 13, lineHeight: 17.55 },
  "fine-print-title": { fontSize: 13, lineHeight: 16.9 },
  "fine-print-description": { fontSize: 12, lineHeight: 16.2 },
  "micro-title": { fontSize: 11, lineHeight: 14.85 },
};

type TextBaseProps = ComponentProps<"div"> & {
  children: React.ReactNode;
  multiline?: boolean;
  ellipsis?: boolean;
  ellipsisParentFlexDirection?: "row" | "col";
  disableEllipsisTooltip?: boolean;
  // Hover delay for the ellipsis tooltip. The default suits a dense table,
  // where a pointer crossing rows should not trail tooltips; a surface whose
  // truncated text IS the content wants it at once.
  ellipsisTooltipDelayMs?: number;
  inline?: boolean;
  color?: string;
  className?: string;
  style?: CSSProperties;
  loading?: boolean;
  skeletonWidth?: number;
  marginTop?: CSSProperties["marginTop"];
  marginBottom?: CSSProperties["marginBottom"];
  marginLeft?: CSSProperties["marginLeft"];
  marginRight?: CSSProperties["marginRight"];
  hoverStyle?: CSSProperties;
  ellipsisParentClassName?: string;
};

type TextTypographyProps =
  | { variant: TextV4Variant; type?: never; fontWeight?: never }
  | { type: TextType; fontWeight?: TextFontWeight; variant?: never };

export type TextProps = TextBaseProps &
  TextTypographyProps & {
    link?: Route | null | undefined;
    externalUrl?: string;
    linkOpenInNewTab?: boolean;
  };

export type TextOverrideProps = Partial<
  TextBaseProps & {
    type: TextType;
    fontWeight: TextFontWeight;
    link: Route | null | undefined;
    externalUrl: string;
    linkOpenInNewTab: boolean;
  }
> & { variant?: never };

const Text: FC<TextProps> = (props) => {
  const { link, externalUrl, linkOpenInNewTab, ...rest } = props;
  if (link || externalUrl) {
    const defaultHoverStyle: CSSProperties = {
      textDecoration: "underline",
    };
    const inner = (
      <TextInner
        {...rest}
        hoverStyle={{ ...defaultHoverStyle, ...props.hoverStyle }}
      />
    );
    if (externalUrl) {
      return (
        <a
          href={externalUrl}
          className="flex no-underline text-[unset]"
          target={linkOpenInNewTab ? "_blank" : undefined}
          rel={linkOpenInNewTab ? "noreferrer" : undefined}
          style={{ ...props.style }}
        >
          {inner}
        </a>
      );
    }
    return (
      <Link
        href={link!}
        className="flex no-underline text-[unset]"
        target={linkOpenInNewTab ? "_blank" : undefined}
        rel={linkOpenInNewTab ? "noreferrer" : undefined}
        style={{ ...props.style }}
      >
        {inner}
      </Link>
    );
  } else {
    return <TextInner {...rest} />;
  }
};

type TextInnerProps = TextBaseProps & {
  type?: TextType;
  fontWeight?: TextFontWeight;
  variant?: TextV4Variant;
};

const TextInner: FC<TextInnerProps> = ({
  type,
  variant,
  children,
  fontWeight,
  color,
  multiline,
  ellipsis,
  ellipsisParentFlexDirection,
  inline,
  className,
  style,
  disableEllipsisTooltip,
  ellipsisTooltipDelayMs,
  loading,
  skeletonWidth = 100,
  marginBottom,
  marginTop,
  marginLeft,
  marginRight,
  hoverStyle,
  ellipsisParentClassName,
  ...props
}) => {
  if (ellipsis && multiline) {
    console.warn("Using ellipsis and multiline does not make sense.");
  }

  const defaultFontWeight =
    fontWeight ||
    (type && ["title", "H1", "H2", "H3", "H4"].includes(type)
      ? "SemiBold"
      : "Regular");

  const lineHeight = variant
    ? `${textV4VariantMetrics[variant].lineHeight}px`
    : textTypeToLineHeight[type!];
  const fontSize = variant
    ? textV4VariantMetrics[variant].fontSize
    : textTypeToFontSize[type!];

  const margin = { marginTop, marginBottom, marginLeft, marginRight };
  const textTypeStyles = useMemo(
    () =>
      cn(
        variant
          ? textV4Variants({
              variant,
              inline: inline ? true : false,
              clickable: props.onClick != null ? true : false,
            })
          : textVariants({
              variant: type,
              fontWeight: defaultFontWeight,
              inline: inline ? true : false,
              clickable: props.onClick != null ? true : false,
            }),
        className,
      ),
    [variant, defaultFontWeight, inline, className, type, props.onClick],
  );
  const [isHovered1, hoverBind1] = useHover();
  const [isHovered2, hoverBind2] = useHover();
  const [isHovered3, hoverBind3] = useHover();
  if (loading && !children) {
    return (
      <div
        {...hoverBind1}
        className="flex items-center transition-all duration-150 ease-bezier"
        style={{
          height: lineHeight,
          ...(isHovered1 ? hoverStyle : {}),
        }}
      >
        <DelayedSkeleton height={fontSize} width={skeletonWidth} />
      </div>
    );
  } else if (multiline) {
    return (
      <div
        {...hoverBind2}
        {...props}
        style={{
          color: color ? color : undefined,
          lineHeight: variant ? undefined : lineHeight,
          ...margin,
          ...(isHovered2 ? hoverStyle : {}),
          wordBreak: "break-word",
          ...style,
        }}
        className={textTypeStyles}
      >
        {children}
      </div>
    );
  } else if (ellipsis) {
    return (
      <EllipsisText
        {...props}
        lineHeight={lineHeight}
        style={{
          color: color ? color : undefined,
          ...margin,
          ...style,
        }}
        className={textTypeStyles}
        parentClassName={ellipsisParentClassName}
        parentFlexDirection={ellipsisParentFlexDirection || "col"}
        disableEllipsisTooltip={disableEllipsisTooltip}
        delayDuration={ellipsisTooltipDelayMs}
        hoverStyle={hoverStyle}
      >
        {children}
      </EllipsisText>
    );
  } else {
    return (
      <span
        {...hoverBind3}
        {...props}
        style={{
          color: color ? color : undefined,
          whiteSpace: "nowrap",
          ...margin,
          ...(isHovered3 ? hoverStyle : {}),
          ...style,
        }}
        className={textTypeStyles}
      >
        {children === "" || children === null || children === undefined
          ? " "
          : children}
      </span>
    );
  }
};

type EllipsisTextProps = ComponentProps<"div"> & {
  style?: CSSProperties;
  color?: string;
  lineHeight: CSSProperties["lineHeight"];
  children: React.ReactNode;
  className?: string;
  parentClassName?: string;
  parentFlexDirection: "row" | "col";
  disableEllipsisTooltip?: boolean;
  delayDuration?: number;
  hoverStyle?: CSSProperties;
};

const EllipsisText: FC<EllipsisTextProps> = ({
  className,
  style,
  color,
  lineHeight,
  children,
  parentFlexDirection,
  disableEllipsisTooltip,
  delayDuration = 500,
  hoverStyle,
  parentClassName,
  ...props
}) => {
  const ref = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const setShowTooltipWrapper = useCallback(() => {
    setShowTooltip(
      // @ts-ignore
      (ref?.current?.offsetWidth || 0) < (ref?.current?.scrollWidth || 0),
    );
  }, [setShowTooltip]);
  useEffect(() => {
    setTimeout(setShowTooltipWrapper, 0);
  });
  useOnWindowResize(setShowTooltipWrapper);
  const [isHovered, hoverBind] = useHover();

  return (
    <RadixTooltip.Root delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild>
        <div
          {...hoverBind}
          {...props}
          style={{
            color,
            position: "relative",
            flex: parentFlexDirection === "row" ? 1 : undefined,
            alignSelf: parentFlexDirection === "col" ? "stretch" : undefined,
            minWidth: 50,
            height: lineHeight,
            maxHeight: lineHeight,
            minHeight: lineHeight,
            ...style,
          }}
          className={cn(
            "transition-all duration-150 ease-bezier",
            parentClassName,
          )}
        >
          <div
            ref={ref}
            className={className}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              verticalAlign: "center",
              ...style,
              ...(isHovered ? hoverStyle : {}),
            }}
          >
            {children}
          </div>
        </div>
      </RadixTooltip.Trigger>
      {!disableEllipsisTooltip && (
        <RadixTooltip.Portal>
          <RadixTooltip.Content align={"start"} style={{ zIndex: 500 }}>
            {showTooltip && (
              <div className="bg-white px-1 rounded border border-gray-5-alpha">
                <Text variant="fine-print-description" className="text-gray-10">
                  {children}
                </Text>
              </div>
            )}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      )}
    </RadixTooltip.Root>
  );
};

const textTypeToLineHeight: Record<TextType, CSSProperties["lineHeight"]> = {
  title: "42px",
  H1: "34px",
  H2: "28px",
  H3: "25px",
  H4: "22px",
  P1: "22px",
  P2: "20px",
  P3: "17px",
  P4: "10px",
  ["training-content"]: "125%",
};
const textTypeToFontSize: Record<TextType, number> = {
  title: 42,
  H1: 24,
  H2: 20,
  H3: 18,
  H4: 18,
  P1: 16,
  P2: 14,
  P3: 12,
  P4: 10,
  ["training-content"]: 20,
};

export default Text;
